import api from '@cannect/services/api'
import type {
  AdhesionPreview,
  ApiEnvelope,
  CreateFromOrderResponse,
  EligibilityView,
  OrderRecurrencyAdhesion,
  RecurrencyCard,
  RecurrencyTerms,
  SubscriptionDetail,
  SubscriptionPreview,
  SubscriptionPreviewBody,
  SubscriptionStatus,
  SubscriptionView
} from './types'

const P = '/my-account/recurrency'

export type EligibilityParams = {
  productIds: number[]
  quantities?: number[]
  cycleDays?: number | null
  personId?: number
  prices?: boolean
  // 009: só a PDP e a sondagem do carrinho mandam `checkout` (motivos de
  // documento viram pendência). O "Incluir produto" da 008 não manda.
  origin?: 'checkout'
}

export const getEligibility = async ({
  productIds,
  quantities,
  cycleDays,
  personId,
  prices,
  origin
}: EligibilityParams) => {
  const params: Record<string, string> = { productIds: productIds.join(',') }

  if (quantities?.length) params.quantities = quantities.join(',')
  if (cycleDays) params.cycleDays = String(cycleDays)
  if (personId) params.personId = String(personId)
  if (prices) params.prices = 'true'
  if (origin) params.origin = origin

  const { data } = await api.get<ApiEnvelope<EligibilityView>>(`${P}/eligibility`, { params })
  return data.data
}

export const getTerms = async () => {
  const { data } = await api.get<ApiEnvelope<RecurrencyTerms>>(`${P}/terms`)
  return data.data
}

export const listSubscriptions = async (status?: SubscriptionStatus) => {
  const { data } = await api.get<ApiEnvelope<SubscriptionView[]>>(`${P}/subscriptions`, {
    params: status ? { status } : undefined
  })
  return data.data
}

export const getSubscription = async (id: number) => {
  const { data } = await api.get<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}`)
  return data.data
}

export const patchSubscription = async (
  id: number,
  body: { items?: Array<{ productId: number; quantity: number }>; cycleDays?: number; cardId?: number }
) => {
  const { data } = await api.patch<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}`, body)
  return data
}

export const previewSubscription = async (id: number, body: SubscriptionPreviewBody) => {
  const { data } = await api.post<ApiEnvelope<SubscriptionPreview>>(`${P}/subscriptions/${id}/preview`, body)
  return data.data
}

export const addSubscriptionItem = async (id: number, body: { productId: number; quantity: number }) => {
  const { data } = await api.post<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}/items`, body)
  return data
}

export const removeSubscriptionItem = async (id: number, productId: number) => {
  const { data } = await api.delete<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}/items/${productId}`)
  return data
}

export const skipSubscription = async (id: number) => {
  const { data } = await api.post<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}/skip`)
  return data
}

export const postponeSubscription = async (id: number, body: { nextChargeAt: string }) => {
  const { data } = await api.post<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}/postpone`, body)
  return data
}

export const pauseSubscription = async (id: number, body: { until: string }) => {
  const { data } = await api.post<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}/pause`, body)
  return data
}

export const resumeSubscription = async (id: number) => {
  const { data } = await api.post<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}/resume`)
  return data
}

export const cancelSubscription = async (id: number, body: { reason?: string }) => {
  const { data } = await api.post<ApiEnvelope<SubscriptionDetail>>(`${P}/subscriptions/${id}/cancel`, body)
  return data
}

export const previewFromOrder = async (orderId: number, body: { cycleDays?: number }) => {
  const { data } = await api.post<ApiEnvelope<AdhesionPreview>>(
    `${P}/subscriptions/from-order/${orderId}/preview`,
    body
  )
  return data.data
}

export const createFromOrder = async (
  orderId: number,
  body: { cycleDays: number; cardId: number; termsVersion: string }
) => {
  const { data } = await api.post<CreateFromOrderResponse>(`${P}/subscriptions/from-order/${orderId}`, body)
  return data
}

export const listRecurrencyCards = async () => {
  const { data } = await api.get<ApiEnvelope<RecurrencyCard[]>>(`${P}/cards`)
  return data.data
}

// O detalhe do pedido do portal carrega por /order_status, que não traz a adesão.
export const getOrderRecurrencyAdhesion = async (orderId: number) => {
  const { data } = await api.get<{ success: boolean; order: { recurrencyAdhesion?: OrderRecurrencyAdhesion | null } }>(
    `/my-account/orders/${orderId}`
  )
  return data.order?.recurrencyAdhesion ?? null
}

export type SubscriptionDocumentType = 'prescription' | 'anvisa'

// Envio de documento a partir de "Minhas assinaturas" (008, research R5).
// Rota vinculada à assinatura (apiCannect 028): grava a receita/Anvisa da pessoa
// da assinatura e abre a pendência "Receita enviada" para o acolhimento.
// A data de emissão é pedida no diálogo, mas a rota não a usa.
export const uploadSubscriptionDocument = async ({
  subscription,
  type,
  file
}: {
  subscription: Pick<SubscriptionView, 'id' | 'isDependent'>
  type: SubscriptionDocumentType
  file: File
  emission?: string
}) => {
  if (subscription.isDependent) throw new Error('DEPENDENT_UPLOAD_NOT_SUPPORTED')

  const form = new FormData()
  form.append('documentType', type)
  form.append('file', file)

  const { data } = await api.post<{ success: boolean; message?: string }>(
    `${P}/subscriptions/${subscription.id}/documents`,
    form
  )
  return data
}
